import React, { createContext, useContext, useState, useEffect } from 'react';
import { buildApiUrl } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const getRole = (user) => {
  if (!user) return '';
  const rawRole = user.Role || user.role || user.userType || user.user_type || '';
  const normalizedRole = String(rawRole).toLowerCase().trim();

  if (['student', 'user'].includes(normalizedRole)) {
    return 'user';
  }

  if (['teacher', 'admin', 'super_admin'].includes(normalizedRole)) {
    return 'admin';
  }

  if (user.IsAdmin === true || user.isAdmin === true) {
    return 'admin';
  }

  if (user.TeacherID || user.teacherId) {
    return 'admin';
  }

  if (user.StudentID || user.studentId) {
    return 'user';
  }

  return '';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch (err) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }

    setLoading(false);
  }, []);

  const handleLoginSuccess = (data) => {
    const rawUser = data.user || {};
    const effectiveRole = data.role || data.userType || rawUser.Role || rawUser.role || '';
    const userWithRole = {
      ...rawUser,
      role: rawUser.role || effectiveRole,
      Role: rawUser.Role || effectiveRole
    };

    setToken(data.token);
    setUser(userWithRole);
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('authUser', JSON.stringify(userWithRole));

    return {
      ...data,
      user: userWithRole
    };
  };

  const updateStoredUser = (nextUser) => {
    setUser(nextUser);
    localStorage.setItem('authUser', JSON.stringify(nextUser));
  };

  const parseResponse = async (response, fallbackMessage) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || fallbackMessage);
    }
    return data;
  };

  const login = async (email, password) => {
    setError(null);
    const response = await fetch(buildApiUrl('/auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await parseResponse(response, 'Login failed');
    const result = handleLoginSuccess(data);
    const determinedRole = getRole(result.user);
    const redirectPath = determinedRole === 'admin' ? '/admin/dashboard' : '/dashboard';

    return {
      ...result,
      determinedRole,
      redirectPath
    };
  };

  const loginUser = (email, password) => login(email, password);
  const loginTeacher = (email, password) => login(email, password);

  const registerUser = async (email, password, confirmPassword) => {
    setError(null);
    const response = await fetch(buildApiUrl('/auth/user/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, confirmPassword })
    });

    const data = await parseResponse(response, 'Registration failed');
    if (data.token && data.user) {
      handleLoginSuccess(data);
    }
    return data;
  };

  const registerTeacher = async (email, password, confirmPassword) => {
    setError(null);
    const response = await fetch(buildApiUrl('/auth/admin/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, confirmPassword })
    });

    const data = await parseResponse(response, 'Admin registration failed');
    if (data.token && data.user) {
      handleLoginSuccess(data);
    }
    return data;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  const role = getRole(user);
  const isUser = role === 'user';
  const isAdmin = role === 'admin';

  const value = {
    user,
    token,
    loading,
    error,
    login,
    loginUser,
    loginTeacher,
    loginAdmin: login,
    registerUser,
    registerTeacher,
    handleLoginSuccess,
    updateStoredUser,
    logout,
    role,
    isAuthenticated: !!token && !!user,
    isUser,
    isAdmin,
    isStudent: isUser,
    isTeacher: isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
